import { DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PendingRequestSummaryModel } from 'src/app/models/dashboard/pending-request-summary.model';
import { RequestTypeModel } from 'src/app/models/dashboard/request-type.model';
import { TableColumn } from 'src/app/models/table-column.model';
import { DashboardWidgetsDataService } from 'src/app/services/dashboard-widget-data.service';

@Component({
  selector: 'app-pending-requests-widget-widget',
  standalone: false,
  templateUrl: './pending-requests-widget.component.html',
  styleUrl: './pending-requests-widget.component.scss'
})
export class PendingRequestsWidgetComponent {
  requests: PendingRequestSummaryModel[] = [];
  
  requestColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.TYPE'),
      dataField: 'type',
      dataType: 'string',
      disableFiltering: true,
      cellValue: (data: PendingRequestSummaryModel): string => {
        switch (data.type) {
          case RequestTypeModel.CertificateApproval:
            return data.requestsCount + ' ' + this.translateService.instant(
              'APPROVAL-REQUEST.TYPES.CERTIFICATE-APPROVALS'
            );
          case RequestTypeModel.CourseApproval:
            return data.requestsCount + ' ' + this.translateService.instant(
              'APPROVAL-REQUEST.TYPES.COURSE-APPROVALS'
            );
          case RequestTypeModel.Queue:
            return data.requestsCount + ' ' + this.translateService.instant(
              'APPROVAL-REQUEST.TYPES.QUEUE'
            );
          case RequestTypeModel.DraftComplianceCourse:
            return data.requestsCount + ' ' + this.translateService.instant(
              'APPROVAL-REQUEST.TYPES.DRAFT-COMPLIANCE-COURSE'
            );
          case RequestTypeModel.UnmatchedPublishingCategory:
            return data.requestsCount + ' ' + this.translateService.instant(
              'APPROVAL-REQUEST.TYPES.UNMATCHED-PUBLISHING-CATEGORY'
            );
          default:
            return 'n.a.';
        }
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.DATE'),
      dataField: 'date',
      dataType: 'string',
      disableFiltering: false,
      cellValue: (data: any): any => {
        if(!data.date) return null;
        return new Date(data.date);
      },
      calculateSortValue: (data: PendingRequestSummaryModel): Date | string => {
        if (!data.date) {
          return '';
        }

        return new Date(data.date);
      },
      calculateDisplayValue: (data: PendingRequestSummaryModel): string => {
        if (!data.date) {
          return 'N/A';
        }

        return (
          new DatePipe(this.translateService.currentLang).transform(
            new Date(data.date),
            'dd.MM.yyyy'
          ) || ''
        );
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.DETAILS'),
      dataField: 'requestor',
      dataType: 'string',
      cssClass: 'width-200',
      disableFiltering: false,
      cellValue: (data: PendingRequestSummaryModel): string => {
        if(
          data.type == RequestTypeModel.Queue
        ) {
          return data.training?.name ?? '';
        } 
        else if(
          data.type == RequestTypeModel.DraftComplianceCourse
        ) {
          return this.translateService.instant(
            'COMPLIANCE.COMMON.COURSES-STILL-IN-DRAFT'
          )
        } 
        else if(
          data.type == RequestTypeModel.UnmatchedPublishingCategory
        ) {
          if(!data.tenantSubscription) return '';
          return data.tenantSubscription.name ?? '';
        } 
        else {
          if(!data.requestor) return '';
          return data.requestor.firstName + ' ' + data.requestor.lastName
        }
      },
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          text: this.translateService.instant('COMMON.REVIEW'),
          isShowAction: () => true,
          isButton: true,
          onClick: (request: PendingRequestSummaryModel, e: any): void => {
            e.stopPropagation();
            e.preventDefault();
            e.stopImmediatePropagation();
            if(request.type == RequestTypeModel.Queue) {
              this.router.navigate(['training', request.training!.id], {
                queryParams: {
                  'employee-tab': 'queue'
                }
              });
            } 
            else if(request.type == RequestTypeModel.DraftComplianceCourse) {
              this.router.navigate(['manage-compliance'], {
                queryParams: {
                  'deployed': false
                }
              });
            }
            else if(request.type == RequestTypeModel.UnmatchedPublishingCategory) {
              this.router.navigate(['subscriptions'], {
                queryParams: {
                  subscriptionId: request.subscription?.id,
                  'open-settings': true
                }
              });
            }
            else {
              this.router.navigate(['employee', 'edit', request.requestor!.id], {
                  queryParams: {
                    tab: 'trainings'
                  }
                }
              );
            }
          },
        },
      ],
    },
  ];

  constructor(
    private translateService: TranslateService,
    private router: Router,
    private dashboardWidgetsDataService: DashboardWidgetsDataService,
    private datePipe: DatePipe
  ) {
    
  }

  ngOnInit() {
    this.dashboardWidgetsDataService.getPendingRequestData().subscribe(data => {
      if (data) {
        this.requests = data
          .filter(m => m.requestsCount !== 0)
          .sort((a, b) => {
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;

            return new Date(b.date).getTime() - new Date(a.date).getTime()
          })
          .map((value: PendingRequestSummaryModel, index: number) => {
            return {
              id: index,
              ...value
            }
          });
      }
    });
  }
}
